import React, { useEffect, useState } from 'react'
import FbFormikTextField, {
    ContainedButton,
    FbButton,
    GridContainer,
    GridItem,
    Typography
} from '@fabricks/ui-components'
import { Skeleton } from '@material-ui/lab'
import { useKeycloak } from 'react-keycloak'
import { get as _get, keyBy as _keyBy } from 'lodash'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import Swal from 'sweetalert2'
import axios from 'axios'
import { FormikHelpers } from 'formik/dist/types'
import {FormikValues, Formik, Form, Field} from 'formik'
import {
    BoxTypeAggregateDto,
    ProcessLogMetaParamDto, useCalculateNeededBoxesAdvancedMutation, useCurrentAvailableBoxTypesLazyQuery,
    useGetMeQuery,
    useOptimizeBoxFuelLevelsMutation,
} from '../../../api/models'
import { createBaseContext } from '../../../services/apiService'
import getEnvVars from '../../../environment/environment'
import { downloadFile, extractError } from '../../../utils/apiUtils'
import { ProcessOverview } from '../../../components/ProcessOverview/processOverview'
import { FormFeedback, FormGenerator } from '../../../utils/formGenerator'
import { showInfo } from '../../../utils/sweetAlertHelper'
import { neededBoxesFormConfig } from "./neededBoxesFormConfig";
import {FormControlLabel, Switch, TextField, Button} from "@material-ui/core";
import {FbTextFieldFormElement} from "../../../utils/formElements";
import { hashCode } from "../../../utils/formHelper";

interface NeededBoxesCalcProps {
    initLoadingTime?: number
}

export const BoxTypeOptimizationLoading: React.FC = () => {
    return (
        <>
            <Skeleton variant="rect" animation="wave" width="100%" height={200} />
            <Skeleton variant="text" animation="wave" width="100%" height={50} />
            <Skeleton variant="text" animation="wave" width="100%" height={50} />
        </>
    )
}

export const NeededBoxesCalculation: React.FC<NeededBoxesCalcProps> = (props: NeededBoxesCalcProps) => {
    const { initLoadingTime } = props
    const [keycloak] = useKeycloak()
    const [initialValues, setInitialValues] = useState({
        desiredFuelLevel: 80,
        availableBoxes: [],
    })
    const [initLoading, setInitLoading] = useState((initLoadingTime || 0) > 0)
    const [barData, setBarData] = useState<any[]>([])
    const [currentBoxTypeReq, currentBoxTypeResp] = useCurrentAvailableBoxTypesLazyQuery()
    const [fillLevelToConfig, setFillLevelToConfig] = useState(0)
    const [boxesToConfig, setBoxesToConfig] = useState([])
    const [displayConfigForm, setDisplayConfigForm] = useState(false)
    const [calculateBestBoxTypeReq] = useCalculateNeededBoxesAdvancedMutation()
    const { data: meData, loading: meLoading, error: meError, called: meCalled } = useGetMeQuery(
        createBaseContext(keycloak.token || '')
    )

    useEffect(() => {
        if (keycloak.token && !currentBoxTypeResp.called) {
            currentBoxTypeReq(createBaseContext(keycloak.token))
        }
        if (currentBoxTypeResp.called && currentBoxTypeResp.data) {
            const currentBoxTypeData = _get(currentBoxTypeResp, 'data.getStatistics.aggregateAvailableBoxTypes', []).map(
                (entry: BoxTypeAggregateDto) => {
                    const name = _get(entry, 'boxType.name', '-')
                    const uuid = _get(entry, 'boxType.uuid', '-')
                    // @ts-ignore
                    const newVal = 0
                    return {
                        name,
                        uuid,
                        all: _get(entry, 'count', 0),
                    }
                }
            )
            setBarData(currentBoxTypeData)
        }
        return () => {}
    }, [currentBoxTypeReq, currentBoxTypeResp, keycloak.token])

    useEffect(() => {
        if (meError) {
            alert('Your desired filling level could not be determined')
        }

        if (meCalled && !meLoading) {
            const targetValue = _get(meData, 'getMe.data.0.tenant.config.desiredFuelLevel', null)
            if (targetValue) {
                setInitialValues(Object.assign(initialValues, { desiredFuelLevel: targetValue * 100 }))
            }
        }
        return () => {}
    }, [meData, meError, meCalled, meLoading, initialValues])


    const onSubmit = (
        values: any,
        actions: FormikHelpers<FormikValues>,
        setFeedback: React.Dispatch<React.SetStateAction<FormFeedback>>
    ): void => {
        const params = createBaseContext(keycloak.token || '', {
            fuelLevel: values.desiredFuelLevel / 100,
            availableBoxes: values.availableBoxes,
            maxAmountBoxes: values.maxAmountBoxes
        })
        const submitForm = (): void => {
            if(values.availableBoxes.length > 0){
                setDisplayConfigForm(true)
                setBoxesToConfig(values.availableBoxes)
                setFillLevelToConfig(values.desiredFuelLevel / 100)
                actions.setSubmitting(false)
            }
        }

        setTimeout(submitForm, 500)

    }

    const handleDownload = (metaUuid: string) => {
        Swal.fire({
            title: `Download`,
            text: 'The optimization result is ready and can be downloaded',
            showLoaderOnConfirm: true,
            icon: 'success',
            confirmButtonText: 'Download',
            preConfirm: () => {
                return axios
                    .get(`${getEnvVars.bff.host}/optimization/needed-boxes/${metaUuid}`, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            Authorization: keycloak.token ? `Bearer ${keycloak.token}` : '',
                        },
                        responseType: 'blob',
                    })
                    .then(downloadFile)
            },
        })
    }

    if (initLoading) {
        setTimeout(() => setInitLoading(false), initLoadingTime)
        return <BoxTypeOptimizationLoading />
    }

    const customAxisTick = (props: any) => {
        const { x, y, stroke, payload } = props
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                    {payload.value}
                </text>
            </g>
        )
    }

    const renderChart = () => {
        return (
            <ResponsiveContainer width="100%" height={355} className="box-type-opt-chart-container">
                <BarChart
                    className="box-type-opt-current-chart"
                    height={350}
                    data={barData}
                    margin={{
                        top: 10,
                        right: 15,
                        left: -10,
                        bottom: 70,
                    }}
                >
                    <CartesianGrid strokeDasharray="4 4" />
                    <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        tick={customAxisTick}
                        tickCount={barData.length}
                        tickSize={barData.length}
                    />
                    <YAxis stroke="#333" />
                    <Tooltip labelStyle={{ color: '#000' }} />
                    <Legend layout="horizontal" verticalAlign="top" align="center" margin={{ bottom: 70 }} height={40} wrapperStyle={{marginBottom: 15}} />
                    <Bar dataKey="best" fill="#32ff7e" />
                </BarChart>
            </ResponsiveContainer>
        )
    }

    const onRowClick = (uuid: string, data: any) => {
        const isLoading = _get(data, 'status', null) === 'IN_PROGRESS'
        const meta = _get(data, 'meta', null)
        if (isLoading) {
            return showInfo('The process is not done yet. We will try to accelerate the process', 'Not ready')
        }
        if (meta) {
            return handleDownload(meta)
        }
    }

    const onShowResult = (result: ProcessLogMetaParamDto[]) => {
        const bestAmount = JSON.parse(_get(result, '0.value', {}))
        const newBarData: any[] = barData.map((entry: any) => {
            // eslint-disable-next-line no-param-reassign
            entry.best = _get(bestAmount, entry.uuid, 0)
            return entry
        })
        setBarData(newBarData)
    }

    const boxes = boxesToConfig.map((uuid) => {
        return {
            uuid: uuid,
            maxPerPlace: 1,
            maxSlots: 20000,
            maxBoxes: 20000,
        }
    })

    const renderConfigForm = () => {
        if(displayConfigForm && boxesToConfig.length > 0) {
            return (
                <Formik
                    key={hashCode(JSON.stringify(initialValues))}
                    enableReinitialize={true}
                    initialValues={{
                        boxes
                    }}
                    onSubmit={async (values:any) => {
                        await new Promise((r) => setTimeout(r, 500));
                        const parameter = createBaseContext(keycloak.token || '', {
                            fillingLevel: fillLevelToConfig,
                            selectedBoxes: values.boxes.map((box: any) => {
                                return {
                                    "boxTypeUuid": box["uuid"],
                                    "maxPerPlace": box["maxPerPlace"],
                                    "maxSlots": box["maxSlots"],
                                    "maxBoxes": box["maxBoxes"],
                                }
                            })
                        })
                        calculateBestBoxTypeReq(parameter)
                            .then((response: any) => {
                                const error = extractError(response, 'calculateNeededBoxes')
                                if (error) {
                                    Swal.fire({
                                        title: `ERROR (${error.code})`,
                                        text: `${_get(error, 'message', 'unknown')} (${_get(error, 'code', 'XXX')})`,
                                        icon: 'error',
                                        confirmButtonText: 'Ok',
                                    })
                                    return
                                }
                                Swal.fire({
                                    title: 'SUCCESS',
                                    text: 'The needed box types were successfully triggered',
                                    icon: 'success',
                                    confirmButtonText: 'Ok',
                                })
                            })
                    }}
                >
                    <Form className="generalConfig">
                        {renderConfigElements()}
                        <Button className="MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary superSave" type="submit">Save configuration</Button>
                    </Form>
                </Formik>
            )

            return ""
        }
    }

    const renderConfigElements = () => {
        if(displayConfigForm) {
            return boxes.map((element:any, index: number) => {
                    const boxName = barData.map((box:any)=>{
                        if(box.uuid == element.uuid){
                            return box.name
                        }
                        return ""
                    })
                    return (
                        <>
                            {(() => {
                                if (index == 0) {
                                    return (
                                        <div className="formGroup" style={{display: "flex"}}>
                                            <div>&nbsp;</div>
                                            <div>Max per place</div>
                                            <div>Max slots</div>
                                            <div>Max boxes</div>
                                        </div>
                                    )
                                }
                            })()}

                        <div className="formGroup" key={"group_" + index} style={{flexDirection: "row"}}>
                            <div>{boxName}</div>
                            <Field type="number" id={`boxes[${index}].maxPerPlace`} name={`boxes[${index}].maxPerPlace`}  required/>
                            <Field type="number" id={`boxes[${index}].maxSlots`}  name={`boxes[${index}].maxSlots`} required/>
                            <Field type="number" id={`boxes[${index}].maxBoxes`}  name={`boxes[${index}].maxBoxes`} required/>
                        </div>
                        </>
                    )
                })
        }

        return ""
    }

    return (
        <div style={{ marginBottom: '15px' }}>
            <div>{renderChart()}</div>
            <br />
            <br />
            <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                    <div style={{ position: 'relative' }}>
                        <FormGenerator
                            id={`${hashCode(JSON.stringify(initialValues))}`}
                            config={neededBoxesFormConfig}
                            submitLabel="Create Configuration"
                            initialValues={initialValues}
                            onSubmit={onSubmit}
                        />
                    </div>
                    <div style={{ position: 'relative' }}>
                        {renderConfigForm()}
                    </div>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                    <ProcessOverview type={'PREPROCESS_NEEEDED_BOXES'} onShowResult={onShowResult} />
                </GridItem>
            </GridContainer>
        </div>
    )
}
